import { PlatformLocation } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { logFeature, logRequest } from '@office-start/telemetry-service';
import { KnownFeatureNames, KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { Observable, Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { HttpStatusCode } from '../shared/constants/http-status-code.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../shared/instrumentation/instrumented-http.service';
import { HttpRequestOptions } from '../shared/models/http-request-options.model';
import { AadAppId, AAD_APP_ID } from '../shared/services/aad-app-id.injection-token';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { EdgeworthConfigService } from '../shared/services/edgeworth-config.service';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { OfficeHomeApiConfigService } from '../shared/services/officehome-api-config.service';
import { Performance } from '../shared/services/performance.service';
import { SchedulePriority, SchedulerService } from '../shared/services/scheduler.service';
import { WindowService } from '../shared/services/window.service';
import { XhrService } from '../shared/services/xhr.service';
import { ClientStartupConfig, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { GuidGenerator } from '../shared/utilities/guid-generator.utility';
import { EdgeworthDataEvent, EdgeworthSubstrateDeltaSyncFile, EdgeworthSubstrateFile, localeParam, RawEdgeworthDeltaSyncResponse, RawEdgeworthSubstrateDeltaSyncFile, RecentActivity, syncTimestampStorageKeyPrefix } from './edgeworth-data.model';
import { createRequestConfig, getServerDebugInfoString, processUserRelationship, safeParseDate } from './edgeworth-data.utility';

interface EdgeworthDeltaSyncDataResponse {
    readonly hasFailures: boolean;
    readonly items: ReadonlyArray<EdgeworthSubstrateFile>;
    readonly nextPageLink?: string;
    readonly version: number;
}

@Injectable({
    providedIn: 'root'
})
export class EdgeworthSubstrateDeltaSyncService extends XhrService {
    public get syncTimestampUpdated(): Observable<void> {
        return this._syncTimestampUpdated.asObservable();
    }

    // LocalStorage key used for the last Edgeworth deltasync
    private readonly syncTimestampStorageKey = `${syncTimestampStorageKeyPrefix}.${this.windowService.nativeWindow.ewDbVersion}`;
    private readonly endpoint: string;
    private readonly _syncTimestampUpdated: Subject<void> = new Subject();
    private serverSyncTimestamp: string;
    private readonly startupConfig: ClientStartupConfig;
    private initialFullSyncCompleted = false;

    constructor(
        private readonly localStorageService: LocalStorageService,
        private readonly windowService: WindowService,
        readonly instrumentationService: InstrumentationService,
        accessTokenService: AccessTokenService,
        contextService: ContextService,
        instrumentedHttp: InstrumentedHttp,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        startupConfigService: ClientStartupConfigService,
        scheduler: SchedulerService,
        @Inject(AAD_APP_ID) private readonly aadAppId: AadAppId,
        edgeworthConfigService: EdgeworthConfigService
    ) {
        super(
            accessTokenService,
            instrumentationService,
            instrumentedHttp,
            AccessTokenConsumer.Substrate,
            contextService,
            officeHomeApiConfigService,
            platformLocation,
            scheduler
        );
        this.startupConfig = startupConfigService.config;
        const config = edgeworthConfigService.config;
        if (config) {
            const apiHost = config.substrateApiEndpoint.indexOf('/') === 0 ? `${this.windowService.nativeWindow.location.origin}${config.substrateApiEndpoint}` : config.substrateApiEndpoint;
            this.endpoint = `${apiHost}${config.edgeworthApiRootPath}${config.deltaSyncApiPath}?${localeParam}${this.contextService.sharedContext.officeMarket}`;
        }
    }

    public syncDocuments(): Observable<EdgeworthDataEvent> {
        this.serverSyncTimestamp = null;
        return new Observable<EdgeworthDataEvent>(observer => {
            (async () => {
                const syncStartMark = Performance.now();
                const url = new URL(this.endpoint);
                const lastSyncTime = this.readLastSyncTimestamp();
                const syncId = GuidGenerator.createGuid();

                if (lastSyncTime) {
                    url.searchParams.append('startDateTime', lastSyncTime);
                }

                let endpoint = url.toString();
                let pageNumber = 1;
                let itemCount = 0;

                while (true) {
                    let page: EdgeworthDeltaSyncDataResponse;
                    try {
                        page = await this.getPage(endpoint, pageNumber, syncId);
                    } catch (e) {
                        observer.error(e);
                        return;
                    }

                    const { items, nextPageLink, hasFailures, version } = page;

                    if (items?.length && !this.serverSyncTimestamp) {
                        itemCount += items.length;
                        const [ latestItem ] = items;

                        if (version === 2) {
                            this.serverSyncTimestamp = latestItem.last_store_modified_datetime;
                        } else {
                            this.serverSyncTimestamp = latestItem.last_recommended_modified_datetime;
                        }
                    }

                    observer.next({ items, hasPluginFailures: hasFailures });
                    const nextPageEndpoint = nextPageLink ? `${this.endpoint}&${nextPageLink}` : undefined;

                    // There are a few conditions which we want to do a hard stop from fetching:
                    // - nextPageLink is falsy, so there's no subsequent page
                    // - server returns the same nextPageLink, so we are calling the exact same request again
                    // - pageNumber is over 40 (backend has a hard limit of ~4000 documents)
                    if (!nextPageEndpoint || endpoint === nextPageEndpoint || pageNumber > 40) {
                        break;
                    }

                    ++pageNumber;

                    endpoint = nextPageEndpoint;
                }

                if (!this.initialFullSyncCompleted) {
                    // only instrument full sync event once
                    const syncEndMark = Performance.now();
                    const latency = syncEndMark - syncStartMark;
                    const params = {[
                        'ActualSize']: itemCount.toString(),
                        ['ItemId']: syncId,
                        ['ItemCount']: pageNumber.toFixed()
                    };
                    this.instrumentationService.logRequestEvent(
                        KnownInstrumentationEvents.EdgeworthFullSyncRequest,
                        HttpStatusCode.OK,
                        latency,
                        null,
                        false,
                        params
                    );
                    // TODO: Remove above Aria logging after fully migrating to OTEL
                    logRequest({
                        eventName:
                            KnownRequestInstrumentationEvents.EdgeworthFullSyncRequest,
                        httpCode: HttpStatusCode.OK,
                        latency,
                        fromCache: false
                    });
                    logFeature({
                        featureName: KnownFeatureNames.Edgeworth,
                        eventName: 'EdgeworthFullSyncRequest',
                        params
                    });
                    this.initialFullSyncCompleted = true;
                }

                observer.complete();
            })();
        }).pipe(shareReplay(1));
    }

    public markSyncCompleted() {
        if (this.serverSyncTimestamp) {
            const storageKey = this.getStorageKey();
            this.localStorageService.set(storageKey, this.serverSyncTimestamp);
            this._syncTimestampUpdated.next();
            this.serverSyncTimestamp = null;
        }
    }

    public resetTimestamp() {
        this.serverSyncTimestamp = null;
        this.localStorageService.remove(this.getStorageKey());
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mockmru' && this.mockRequestHeader) {
            requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private readLastSyncTimestamp(): string {
        const storageKey = this.getStorageKey();
        const storedServerTimestamp = this.localStorageService.get(storageKey);

        return storedServerTimestamp;
    }

    private async getPage(url: string, pageNumber: number, syncId: string): Promise<EdgeworthDeltaSyncDataResponse> {
        return new Promise<EdgeworthDeltaSyncDataResponse>((resolve, reject) => {
            const promiseCallback = (promise: InstrumentedHttpPromise<RawEdgeworthDeltaSyncResponse>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.ActualSize] = (result?.data?.files ? result.data.files.length : -1).toString();
                    params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    params[InstrumentationParam.Index] = pageNumber.toFixed();
                    params[InstrumentationParam.ImpressionId] = syncId;
                    params[InstrumentationParam.DebugInfo] = getServerDebugInfoString(result);
                })
                .then(result => {
                    const hasFailures = !result.data?.is_complete;
                    resolve({
                        items: this.parse(result.data.files || []),
                        nextPageLink: result.data.next_page_link,
                        hasFailures,
                        version: parseInt(result.data?.diagnostics_data?.extended_info?.Version) || 0
                    });

                    // Handle file deletions in IDB
                    this.scheduler.scheduleWork(() => {
                        const deletePromises = result.data.files?.filter(file => file.is_deleted).map(file => new Promise<void>((resolveDelete, rejectDelete) => {
                            this.scheduler.scheduleWork(() => {
                                this.windowService.nativeWindow.ewIdb?.deleteItem(file.id).then(resolveDelete).catch(rejectDelete);
                                //TODO: thming - delete meeting object from idb when meeting object supports is_deleted
                            }, SchedulePriority.VeryLow);
                        })) || [];
                        Promise.all(deletePromises).catch(err => {
                            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.EdgeworthCacheDeleteError, {
                                [InstrumentationParam.Error]: err?.toString?.() || ''
                            });
                        });
                    }, SchedulePriority.VeryLow);
                });
            };

            this.get(
                KnownInstrumentationEvents.EdgeworthSubstrateDeltaSyncPageRequest,
                KnownRequestInstrumentationEvents.EdgeworthSubstrateDeltaSyncPageRequest,
                url,
                promiseCallback,
                reject,
                createRequestConfig(
                    `${this.startupConfig.objectId}@${this.startupConfig.tenantId}`,
                    this.contextService.sharedContext.recommendedIceApplicationId.toString(),
                    this.startupConfig.objectId,
                    this.aadAppId,
                    GuidGenerator.createGuid(),
                    this.contextService.getCurrentPageForLogging()),
                FetchStrategy.NetworkOnly
            );
        });
    }

    private parse(items: ReadonlyArray<RawEdgeworthSubstrateDeltaSyncFile>): ReadonlyArray<EdgeworthSubstrateDeltaSyncFile> {
        return items.map(file => {
            const userRelationship = file.user_relationship;

            const activities: ReadonlyArray<RecentActivity> = file.activities ? file.activities.map(rawActivity => {
                let activity;
                try {
                    activity = JSON.parse(rawActivity.a);
                } catch {
                    console.log(`Error parsing activity: ${rawActivity.a}`);
                }

                return {
                    ...rawActivity,
                    t: new Date(rawActivity.t),
                    da: {
                        ...rawActivity.da,
                        t: new Date(rawActivity.da.t)
                    },
                    a: activity
                };
            }) : [];
            const activityBadge = file.activity_badge ? {
                ...file.activity_badge,
                timestamp: file.activity_badge.timestamp ? new Date(file.activity_badge.timestamp) : undefined // Local file currently does not have timestamp
            } : undefined;

            return {
                ...file,
                last_recommended_modified_datetime: file.last_recommended_modified_datetime,
                last_store_modified_datetime: file.last_store_modified_datetime,
                user_relationship: processUserRelationship(userRelationship),
                activities,
                activity_badge: activityBadge,
                created_datetime: safeParseDate(file.created_datetime),
                creation_info: file.creation_info ? {
                    ...file.creation_info,
                    timestamp: safeParseDate(file.creation_info.timestamp)
                } : null,
                indexed: false
            };
        });
    }

    private getStorageKey(): string {
        return this.syncTimestampStorageKey;
    }
}
